import { ajax } from 'rxjs/ajax'
import { retryWhen, delay, tap } from 'rxjs/operators'

export class HttpClient {
  constructor({ baseUrl, crossDomain, storage, keycloak }) {
    this.baseUrl = baseUrl
    this.crossDomain = crossDomain
    this.storage = storage
    this.keycloak = keycloak
  }

  buildUrl(relativeUrl) {
    return relativeUrl.startsWith('/')
      ? this.baseUrl + relativeUrl
      : `${this.baseUrl}/${relativeUrl}`
  }

  buildHeaders(headers = { 'Content-Type': 'application/json' }) {
    const token = this.getAuthorization()
    const userId = this.getUserId()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    if (userId) {
      headers['x-entity-user-guid'] = userId
    }

    return headers
  }

  getAuthorization() {
    return this.keycloak ? this.keycloak?.token : ''
  }

  getUserId() {
    return this.keycloak ? this.keycloak.tokenParsed?.sub : ''
  }

  getBody(resp) {
    return resp.response
  }

  /**
   * Makes a GET request to url with given headers.
   * if url is relative then it will be appended baseUrl
   * @param {string} url - url to make request, relative urls are allowed
   * @param {object} headers - headers to include in request
   * @return {Observable} - Observable of request
   */
  get(url, headers) {
    return this.makeRequest({ url, headers, method: 'GET' })
  }

  /**
   * Makes a POST request to url, with paylaod and headers.
   * if url is relative then it will be appended baseUrl
   * @param {string} url - url to make request, relative urls are allowed
   * @param {object} body - body payload to send with request
   * @param {object} headers - headers to include in request
   * @return {Observable} - Observable of request
   */
  post(url, body, headers) {
    return this.makeRequest({ url, method: 'POST', headers, body })
  }

  /**
   * Makes a Patch request to url, with payload and given headers.
   * if url is relative then it will be appended baseUrl
   * @param {string} url - url to make request, relative urls are allowed
   * @param {object} body - body payload to send with request
   * @param {object} headers - headers to include in request
   * @return {Observable} - Observable of request
   */
  patch(url, body, headers) {
    return this.makeRequest({ url, body, headers, method: 'PATCH' })
  }

    /**
   * Makes a Put request to url, with payload and given headers.
   * if url is relative then it will be appended baseUrl
   * @param {string} url - url to make request, relative urls are allowed
   * @param {object} body - body payload to send with request
   * @param {object} headers - headers to include in request
   * @return {Observable} - Observable of request
   */
  put(url, body, headers) {
    return this.makeRequest({ url, body, headers, method: 'PUT' })
  }

  /**
   * Makes a DELETE request to url, with payload and given headers.
   * if url is relative then it will be appended baseUrl
   * @param {string} url - url to make request, relative urls are allowed
   * @param {object} body - body payload to send with request
   * @param {object} headers - headers to include in request
   * @return {Observable} - Observable of request
   */
  delete(url, body, headers) {
    return this.makeRequest({ url, body, headers, method: 'DELETE' })
  }

  makeRequest({
    url,
    method,
    body,
    headers = {
      'Content-Type': 'application/json'
    }
  }) {
    const fullUrl = this.buildUrl(url)
    const builtHeaders = this.buildHeaders(headers)
    const { crossDomain } = this

    return ajax({
      url: fullUrl,
      method,
      body,
      headers: builtHeaders,
      crossDomain
    }).pipe(
      retryWhen(errors =>
        errors.pipe(
          delay(500),
          tap(error => {
            if (error.status === 0) {
              console.log('Retrying 500 errors...')
            } else {
              throw error
            }
          })
        )
      )
    )
  }

  makeRawRequest({
    url,
    method,
    body,
    headers = {
      'Content-Type': 'application/json'
    },
    crossDomain = true
  }) {
    return ajax({
      url,
      method,
      body,
      headers,
      crossDomain
    })
  }
}
